/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Backdrop, Box, Button, CircularProgress, Chip, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material"
import { collection, doc, addDoc, DocumentReference, query, where, onSnapshot, deleteDoc, getDoc, DocumentSnapshot, updateDoc } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db, functions } from "../../firebase"
import { httpsCallable } from "firebase/functions"
import { tokens } from "../../theme"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import dayjs from "dayjs"
import { MasterSelect } from "../lib/MasterSelect"
import { ArrivalDatetime } from "./lib/ArrivalDatetime"
import { setIndividualStatus } from "./lib/common"
import { FormProps } from "../lib/StepForm"
import FormElement from "../lib/FormElement"

const sendMail = httpsCallable(functions, 'sendMail')
const mailTemplate = (id: string) => {
return `
個体受入の準備を開始しました。
以下リンクより個体情報の入力をお願いします。

https://traceability.rindou.info/individuals/${id}/regist
`
}

const AcceptanceRegistration = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [hunterInfo, setHunterInfo] = useState<any>('')
  const [selectedValue, setSelectedValue] = useState<any>(null)
  const [items, setItems] = useState<Array<object>>([])
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState<boolean>(false)
  const [selectedIndividual, setSelectedIndividual] = useState<any>(null)
  const [forms, setForms] = useState<Array<FormProps>>([])
  const [arrivalForms, setArrivalForms] = useState<Array<FormProps>>([])
  const [individualData, setIndividualData] = useState<Record<string, string|number>>({})
  const [arrivalData, setArrivalData] = useState<Record<string, string|number>>({})
  const individualsRef = collection(db, 'individuals')

  const columns = [
    {
      field: "No",
      headerName: "Mo",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "receptionDatetime",
      headerName: "受付日時",
      flex: 1,
    },
    {
      field: "name",
      headerName: "搬入者名",
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "arrival",
      headerName: "施設到着",
      flex: 1,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderCell: (params: any) => (
        params.row.status === "registed" || params.row.status === "arrived"
          ? <ArrivalDatetime individualId={params.row.id} arrivalDatetime={params.row.arrivalDatetime} />
          : <Typography>入力待ち</Typography>
      ),
    },
    {
      field: "arrivalInfo",
      headerName: "到着情報入力",
      flex: 1,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderCell: (params: any) => (
        (params.row.status === "registed" || params.row.status === "arrived") && params.row.arrivalDatetime ? (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => openArrivalDialog(params.row)}
          >
            到着情報入力
          </Button>
        ) : (
          <Typography>到着後に入力可能</Typography>
        )
      ),
    },
    {
      field: "individualInfo",
      headerName: "個体情報入力",
      flex: 1,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderCell: (params: any) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => openIndividualDialog(params.row)}
        >
          個体情報入力
        </Button>
      ),
    },
    {
      field: "cancel",
      headerName: "キャンセル",
      flex: 1,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderCell: (params: any) => (
        <Chip label="キャンセル" color="default" size="small" onClick={() => cancel(params.row.id)}></Chip>
      ),
    }
  ]

  useEffect(() => {
    if (!individualsRef) return
    const q = query(individualsRef, where("status", "in", ["init", "registed", "arrived"]))
    const unsubscribe = onSnapshot(q, ({ docs }) => {
      setItems(docs.map((_, i) => {
        return ({ No: i+1, ..._.data(), id: _.id })
      }))
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    getDoc(doc(collection(db, 'forms'), 'individual')).then((_: DocumentSnapshot) => {
      if (_.exists()) {
        setForms(_.data().conf.filter((form: FormProps) => form.isHunterElements))
        setArrivalForms(_.data().conf.filter((form: FormProps) => form.isArrivalElements))
      }
    })
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onChange = (values) => {
    getDoc(doc(collection(db, 'hunters'), values.value)).then((_: DocumentSnapshot) => {
      setSelectedValue(values)
      _.exists() && setHunterInfo({ ..._.data(), id: _.id })
    })
  }

  const add = async () => {
    if (!hunterInfo) return
    setProcessing(true)
    setHunterInfo(null)
    addDoc(collection(db, "individuals"), setIndividualStatus({ name: hunterInfo.name, email: hunterInfo.email, receptionDatetime: dayjs().format("YYYY/MM/DD HH:mm") }))
      .then(async (docRef: DocumentReference) => {
        await sendMail({
          to: hunterInfo.email,
          subject: "[りんどう] 個体情報の入力をお願いします",
          type: "text",
          contents: mailTemplate(docRef.id)
        })
        toast.success("個体受入情報を追加しました")
      }).catch(e => {
        toast.error(JSON.stringify(e))
      }).finally(() => {
        setProcessing(false)
      })
  }

  const cancel = async (id: string) => {
    if(!window.confirm("受入情報を削除しますか？")) return
    await deleteDoc(doc(collection(db, "individuals"), id))
      .then(() => {
        toast.success("個体受入情報を削除しました")
      })
      .catch(e => {
        toast.error(JSON.stringify(e))
      })
  }

  const openIndividualDialog = (individual: any) => {
    setSelectedIndividual(individual)
    setIndividualData(individual)
    setDialogOpen(true)
  }

  const handleFormChange = ({name, value}: {name: string, value: string|number}) => {
    setIndividualData({...individualData, [name]: value})
  }

  const saveIndividualData = async () => {
    if (!selectedIndividual) return
    setProcessing(true)
    try {
      await updateDoc(doc(collection(db, "individuals"), selectedIndividual.id), {
        ...individualData,
        status: "registed"
      })
      toast.success("個体情報を保存しました")
      setDialogOpen(false)
      setSelectedIndividual(null)
      setIndividualData({})
    } catch (e) {
      toast.error(JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setSelectedIndividual(null)
    setIndividualData({})
  }

  const openArrivalDialog = (individual: any) => {
    setSelectedIndividual(individual)
    setArrivalData(individual)
    setArrivalDialogOpen(true)
  }

  const handleArrivalFormChange = ({name, value}: {name: string, value: string|number}) => {
    setArrivalData({...arrivalData, [name]: value})
  }

  const saveArrivalData = async () => {
    if (!selectedIndividual) return
    setProcessing(true)
    try {
      await updateDoc(doc(collection(db, "individuals"), selectedIndividual.id), {
        ...arrivalData,
        updatedAt: dayjs().format('YYYY/MM/DD HH:mm')
      })
      toast.success("到着情報を保存しました")
      setArrivalDialogOpen(false)
      setSelectedIndividual(null)
      setArrivalData({})
    } catch (e) {
      toast.error(JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const closeArrivalDialog = () => {
    setArrivalDialogOpen(false)
    setSelectedIndividual(null)
    setArrivalData({})
  }

  return (
    <Box m={5}>
      <Box display="flex" justifyContent="start" alignContent="center">
        <MasterSelect
          name="hunter"
          value={selectedValue}
          optionProps={{ masterName: "hunters", valueKey: "id", labelKey: "name" }}
          onChange={onChange}
          label="狩猟者を選択"
        />
        <Box display="flex" m={1} justifyContent="center">
          <Button variant="contained" color="info" onClick={add}>受入情報追加</Button>
        </Box>
      </Box>
      <Box
        m={2}
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid rows={items} columns={columns} rowHeight={70}/>
      </Box>
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>個体情報入力</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {forms.map((form, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {form.label}
                </Typography>
                <FormElement
                  type={form.type}
                  name={form.name}
                  value={individualData[form.name]}
                  options={form.options}
                  onChange={handleFormChange}
                  text={form.text}
                  disabled={form.disabled}
                  label={form.title}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>キャンセル</Button>
          <Button onClick={saveIndividualData} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={arrivalDialogOpen} onClose={closeArrivalDialog} maxWidth="md" fullWidth>
        <DialogTitle>施設到着情報入力</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {arrivalForms.map((form, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {form.label}
                </Typography>
                <FormElement
                  type={form.type}
                  name={form.name}
                  value={arrivalData[form.name]}
                  options={form.options}
                  onChange={handleArrivalFormChange}
                  text={form.text}
                  disabled={form.disabled}
                  label={form.title}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArrivalDialog}>キャンセル</Button>
          <Button onClick={saveArrivalData} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default AcceptanceRegistration
